import { isServerValidationText } from "src/utils/server-validation";
/**
 * Why a coupon was turned away, as a stable value rather than a sentence.
 *
 * The message is for the customer and will eventually be translated; this is
 * for us. It is what gets tagged on the `coupon-rejected` funnel event, so
 * "how many people are bouncing off a minimum they were never told about"
 * becomes a Sentry filter instead of string-matching English prose.
 */
export type CouponRejectionReason =
  | "expired"
  | "minimum-amount"
  | "lookup-failed"
  | "apply-failed";

export class CouponRejectedError extends Error {
  readonly reason: CouponRejectionReason;
  /** For "minimum-amount": the coupon's minimum, in minor units. */
  readonly minimumCents?: number;

  constructor(
    reason: CouponRejectionReason,
    message: string,
    minimumCents?: number,
  ) {
    super(message);
    this.name = "CouponRejectedError";
    this.reason = reason;
    this.minimumCents = minimumCents;
  }
}

/** The only fields coupon validation actually looks at. */
type UsableCoupon = {
  validUntil: string;
  minimumAmount?: number;
};

type CartContext = {
  /** Cart total in minor units, shipping already removed. */
  orderAmount: number;
  currency: string;
  now?: Date;
};

/**
 * Decide whether a coupon the backend already found can be used on this cart.
 *
 * Both checks previously lived inside the same `try` block as the API call, so
 * the `catch` below them rewrote their messages: it read
 * `err.response.data.message`, which a locally-thrown `Error` does not have,
 * and fell through to the literal string "Invalid coupon code". Every customer
 * who was merely a few euros short — or who held a code that had lapsed — was
 * told their code did not exist.
 *
 * That is why this is a separate function and not a few lines inline. Keeping
 * the network call and the business rules in one try/catch is what caused the
 * bug, and two components had copy-pasted the same mistake.
 */
export function assertCouponUsable(
  coupon: UsableCoupon,
  { orderAmount, currency, now = new Date() }: CartContext,
): void {
  // Expiry first: a dead code stays dead however much the customer spends, and
  // telling them to add more to the cart would be actively misleading.
  if (new Date(coupon.validUntil) < now) {
    throw new CouponRejectedError("expired", "Coupon has expired");
  }

  if (coupon.minimumAmount && orderAmount < coupon.minimumAmount) {
    throw new CouponRejectedError(
      "minimum-amount",
      `Minimum order amount of ${coupon.minimumAmount / 100} ${currency} required`,
      coupon.minimumAmount,
    );
  }
}

/**
 * Turn a failed `check-coupon` request into the error the customer sees.
 *
 * A code that genuinely does not exist already comes back with a translated
 * message from the backend, so that message must win. The fallback is only for
 * the cases where there is nothing to show — a network drop, a throttle, a
 * response with no body.
 */
export function couponLookupError(error: unknown): CouponRejectedError {
  const message = (
    error as { response?: { data?: { message?: string } } } | undefined
  )?.response?.data?.message;

  return new CouponRejectedError(
    "lookup-failed",
    message || COUPON_LOOKUP_FALLBACK,
  );
}

/** couponLookupError's English placeholder, which the UI swaps for its own. */
export const COUPON_LOOKUP_FALLBACK = "Invalid coupon code";

/**
 * The sentence to show the customer for a failed coupon step, or null when
 * there is nothing customer-worthy and the caller should use its translated
 * fallback.
 *
 * `check-coupon` is currency-blind, so a code from another shop passes it and
 * is refused only by `add-coupon`, with a 404 and an already-localized message
 * ("Kupon z kodem TESTCWCZK nie został znaleziony."). That 404 used to escape
 * the field's try/catch: the field cleared and nothing was said (currency
 * walk B2). Axios's own "Request failed with status code 404" is never shown.
 */
export function couponErrorMessage(error: unknown): string | null {
  const server = (
    error as { response?: { data?: { message?: unknown } } } | undefined
  )?.response?.data?.message;
  // class-validator's English ("code should not be empty") is not for a
  // customer: the caller's translated fallback says it better (ops walk B8).
  if (typeof server === "string" && server.trim() && !isServerValidationText(server))
    return server;
  if (
    error instanceof CouponRejectedError &&
    error.message &&
    error.message !== COUPON_LOOKUP_FALLBACK
  ) {
    return error.message;
  }
  return null;
}
